import { useCallback, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { debounce } from "lodash";
import {
  Box,
  Container,
  IconButton,
  makeStyles,
  Typography,
} from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import {
  HeaderWithButtons,
  Searchbar,
  SortButtonAndMenu,
} from "../components-molecules";
import { PromotionCodeList } from "../components-organisms/PromotionCodeList";
import {
  clearPromotionCodes,
  getUsedCodes,
  getUsedCodesByNames,
  getUsedCodesByReferredNames,
  IPromoCodeNameFilterItem,
  IPromotionCodeFilter,
} from "../redux/actions/promotionCodes";
import {
  ISortItem,
  usedCodesFilterOptions as defaultFilters,
  usedCodesSortTypes
} from "../utils/promo-code/headerOptions";
import { OrderSortType } from "../utils/promo-code/groupHeaders";
import { FilterButtonWithTitle } from "../components-molecules/FilterButtonWithTitle";
import { buttonHoverAndActive, textButtonHoverAndActive } from "../utils/styles/buttonStyles";
import { amountSymbol, dateOptions, localeList } from "../utils/promo-code/bottomText";
import { getDiscountType, getUseType } from "../utils";
import { formatDateTime } from "../utils/dateTimeFormatting";
const timeoutDuration = 1500;

export const UsedCodes = (props: any) => {
  const classes = useStyles();
  const [page, setPage] = useState<number>(1);
  const [sortType, setSortType] = useState<OrderSortType>("UsedDate");
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [filterOptions, setFilterOptions] = useState(defaultFilters);
  const [filter, setFilter] = useState<IPromotionCodeFilter>({
    DiscountType: [],
    UseType: [],
    CodeType: [],
    UsedBy: [],
    Referrer: [],
  });
  const [query, setQuery] = useState<string>("");
  const dispatch = useDispatch();
  const [codes]: [any] = useSelector((state: any) => [
    state.PromotionCodesSymbolsReducer.data as any,
  ]);

  const getFilters = () => {
    let newFilters = [...defaultFilters];
    getUsedCodesByNames((res: IPromoCodeNameFilterItem[]) => {
      newFilters.push({
        key: "UsedBy",
        title: "Used By",
        options: res.map(r => ({ value: r.Id, title: `${r.FirstName} ${r.LastName}`, helperText: `${r.PhoneNumber} - ${r.Email}` })),
      })
    }, (err) => {
      console.log(err)
    });

    getUsedCodesByReferredNames((res: IPromoCodeNameFilterItem[]) => {
      newFilters.push({
        key: "Referrer",
        title: "Referred By",
        options: res.map(r => ({ value: r.Id, title: `${r.FirstName} ${r.LastName}`, helperText: `${r.PhoneNumber} - ${r.Email}` })),
      })
    }, (err) => {
      console.log(err)
    });

    setFilterOptions(newFilters);
  }

  const fetchData = useCallback(() => {
    getFilters();
    dispatch(
      getUsedCodes({
        Size: 15,
        Page: page,
        Query: query,
        OrderBy: sortType,
        Filter: filter,
      })
    );
  }, [dispatch, query, sortType, filter, page]);

  useEffect(() => {
    fetchData();
  }, [query, fetchData]);

  const loadMore = () => {
    setPage(page + 1);
  };

  const onFilterSubmit = (thisFilter: any): void => {
    let tempFilter: IPromotionCodeFilter = {
      DiscountType: thisFilter["DiscountType"] as string[],
      UseType: thisFilter["UseType"] as string[],
      CodeType: thisFilter["CodeType"] as string[],
      UsedBy: thisFilter["UsedBy"] as string[],
      Referrer: thisFilter["Referrer"] as string[],
    };
    if (thisFilter["MinimumSpend"]?.length === 1) {
      tempFilter["MinimumSpend"] = thisFilter["MinimumSpend"][0] as boolean;
    }
    if (thisFilter["Status"]?.length === 1) {
      tempFilter["Status"] = thisFilter["Status"][0] as boolean;
    }

    dispatch(clearPromotionCodes());
    setPage(1);
    setFilter(tempFilter as IPromotionCodeFilter);
  };

  const getLoadedCount = () => {
    return codes?.Data?.reduce((p: number, c) => p + c.UsedCodes?.length, 0);
  };

  return (
    <Box className={classes.container}>
      <CssBaseline />
      <Container className={classes.root}>
        <CssBaseline />
        <HeaderWithButtons fullWidthTitle>
          <HeaderWithButtons.ButtonArea>
            <div className={classes.buttonSet}>
              <SortButtonAndMenu
                data-automation="sort-button"
                menuItems={usedCodesSortTypes.map((sortItem: ISortItem) => ({
                  title: sortItem.title,
                  value: sortItem.value,
                  handleSelect: () => {
                    if (sortItem.value === sortType) return;
                    dispatch(clearPromotionCodes());
                    setPage(1);
                    setSortType(sortItem.value);
                  },
                }))}
                selectedSortType={sortType}
              />
              <FilterButtonWithTitle
                defaultOption="DiscountType"
                data-automation="filter-button"
                searchableList={["Referrer", "UsedBy"]}
                onSubmit={onFilterSubmit}
                inButtonSet
                options={filterOptions}
                filter={filter}
              />
              <IconButton
                data-automation="search-button"
                className={classes.searchIconButton}
                color="primary"
                onClick={() => setSearchOpen(!searchOpen)}
              >
                <SearchRoundedIcon
                  color="primary"
                  className={classes.searchIcon}
                />
              </IconButton>
            </div>
          </HeaderWithButtons.ButtonArea>
        </HeaderWithButtons>
        <Searchbar
          query={query}
          isOpen={searchOpen}
          placeholder="Search Promotion Codes"
          onCancel={() => {
            if (query.length > 0) {
              dispatch(clearPromotionCodes());
            }
            setSearchOpen(false);
            setQuery("");
          }}
          onChange={(event) => {
            dispatch(clearPromotionCodes());
            debounce((e) => setQuery(e.target.value), timeoutDuration)(event);
          }}
        />
        <PromotionCodeList
          listKey="UsedCodes"
          query={query}
          filter={filter}
          sortType={sortType}
          onEdit={() => { }}
          loadedCount={getLoadedCount()}
          loadMore={loadMore}
        />
      </Container>
    </Box>
  );
};

export const UsedCodesBottom = ({ item }) => {
  const { t } = useTranslation();
  let text = "";
  const { title, value }: any = getDiscountType(item.DiscountType);
  const useType = getUseType(item.PromoCodeType);
  const amount = value === "Percent" ? item.Percentage : +(item.Amount || 0) / 100;

  text += title;
  if (amount) {
text += ` · ${amountSymbol[value as string](amount, t('currencySymbol'))}`;
  }

  if (item?.MinimumSpend) {
    text += " · ";
text += item.MinimumSpend ? `Min Spend ${t('currencySymbol')}${item.MinimumSpend / 100}` : ``;
  }

  if (useType?.title) {
    text += " · " + useType?.title;
  }

  if (item?.FirstName && item?.LastName && item?.Email) {
    text += ` · User: ${item.FirstName} ${item.LastName} (${item.Email})`;
  }

  if (item?.StartDate) {
    text += ` · Valid ${new Date(item.StartDate).toLocaleDateString(
      localeList,
      dateOptions
    )} 
      to ${new Date(item.Expires).toLocaleDateString(localeList, dateOptions)}`;
  }

  return (
    <div>
      <Typography variant="body2" color="textSecondary">
        {text}
      </Typography>
      {item.UsedByFirstName && item.UsedByLastName &&
        <Typography variant="body2" color="textSecondary">
          <b>Used by:</b> {item.UsedByFirstName} {item.UsedByLastName} ({item.UsedByPhone}{item.UsedByPhone && ` - `}{item.UsedByEmail})
        </Typography>
      }
      {item.UsedOn &&
        <Typography variant="body2" color="textSecondary">
          <b>Used on:</b> {formatDateTime(item.UsedOn)}
        </Typography>
      }
      {item.OrderNumber &&
        <Typography variant="body2" color="textSecondary">
          <b>Order Number:</b> {item.OrderNumber}
        </Typography>
      }
      {item.ReferredByFirstName && item.ReferredByLastName &&
        <Typography variant="body2" color="textSecondary">
          <b>Referred by:</b> {item.ReferredByFirstName} {item.ReferredByLastName} ({item.ReferredByPhone}{item.ReferredByPhone && ` - `}{item.ReferredByEmail})
        </Typography>
      }
    </div>
  );
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    justifyContent: "center",
  },
  root: {
    maxWidth: "840px",
    paddingLeft: 0,
    paddingRight: 0,
  },
  buttonSet: {
    display: "flex",
    flexDirection: "row",
    paddingRight: "24px",
  },
  primaryButton: {
    marginLeft: 8,
    borderRadius: 8,
    ...buttonHoverAndActive
  },
  searchIcon: {
    width: 24,
    height: 24,
  },
  searchIconButton: {
    width: 40,
    height: 40,
    marginLeft: 8,
    ...textButtonHoverAndActive
  },
});
